import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  CATEGORY_CONFIGS,
  CATEGORY_SOURCES,
  getSourceDescription,
  type ParameterCategory,
} from "@/types/parameters.types";
import { StatusMappingEditor } from "./StatusMappingEditor";
import { UnitDropdown } from "./UnitDropdown";
import { VisualRoleFields, limitsInvalid } from "./VisualRoleFields";
import { CounterFields } from "./CounterFields";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import type { ParamForm, SourceKind } from "./types";

interface ParameterFormProps {
  form: ParamForm;
  setForm: (updater: (f: ParamForm) => ParamForm) => void;
  editingId: string | null;
  isPending: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** FR-035 — os contadores já existentes na máquina, para filtrar a lista de produtos. */
  existingCounters: ParameterDetail[];
}

export function ParameterForm({
  form,
  setForm,
  editingId,
  isPending,
  onSubmit,
  onCancel,
  existingCounters,
}: ParameterFormProps) {
  const category = form.category;
  if (!category) return null;

  const config = CATEGORY_CONFIGS.find((c) => c.category === category);
  const allowedSources = CATEGORY_SOURCES[category];
  const showUnit = category === "NUMERIC" || category === "COUNTER" || category === "DURATION";
  const showStatusMapping = category === "STATUS";
  // Só parâmetros STATUS dirigem a transição STOPPED → alarme.
  const showGeneratesAlarm = category === "STATUS";
  // Spec 080 — a marca passou a valer também no booleano (é o relé térmico da fábrica).
  const showIsRestriction = category === "NUMERIC" || category === "BOOLEAN";
  // Spec 081 — o bloco do contador aparece também na EDIÇÃO: produto, tipo de perda e
  // equivalência são corrigíveis; só o sentido vira leitura lá dentro.
  const showCounterFields = category === "COUNTER";

  const submitDisabled =
    isPending ||
    !form.name.trim() ||
    limitsInvalid(form) !== null ||
    (showUnit && !form.unit.trim()) ||
    // Spec 081 — contador de refugo sem tipo de perda seria recusado na rota; barrar aqui evita a
    // viagem e diz o que falta no próprio formulário.
    (form.productionRole === "SCRAPS" && !form.lossType) ||
    (showStatusMapping &&
      (form.statusMapping.length === 0 ||
        form.statusMapping.some((e) => !e.value.trim()) ||
        hasDuplicateKeys(form.statusMapping.map((e) => e.value.trim()).filter(Boolean))));

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <header className="space-y-0.5">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {editingId ? "Editar parâmetro" : "Novo parâmetro"} — {config?.label}
        </h4>
        <p className="text-[10px] text-muted-foreground">{config?.description}</p>
      </header>

      <div className="space-y-1.5">
        <Label htmlFor="p-name">Nome</Label>
        <Input
          id="p-name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          maxLength={120}
        />
      </div>

      {showUnit && (category === "NUMERIC" || category === "COUNTER" || category === "DURATION") ? (
        <div className="space-y-1.5">
          <Label htmlFor="p-unit">Unidade</Label>
          <UnitDropdown
            id="p-unit"
            category={category}
            value={form.unit}
            onChange={(v) => setForm((f) => ({ ...f, unit: v }))}
          />
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="p-source">Fonte</Label>
        <select
          id="p-source"
          value={form.source}
          onChange={(e) => setForm((f) => ({ ...f, source: e.target.value as SourceKind }))}
          // Spec 081 — a fonte deixou de ser travada em Manual: a leitura de sensor passa a
          // alimentar o produzido, e era só a falta desse caminho que obrigava o travamento.
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-60"
        >
          {allowedSources.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-muted-foreground">
          {getSourceDescription(category, form.source)}
        </p>
        {category === "STATUS" && form.source === "MANUAL" ? (
          <p className="rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-[11px] text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-100">
            O status só muda quando o operador registra na aba Operação.
          </p>
        ) : null}
      </div>

      {showStatusMapping ? (
        <StatusMappingEditor
          entries={form.statusMapping}
          onChange={(entries) => setForm((f) => ({ ...f, statusMapping: entries }))}
        />
      ) : null}

      {showGeneratesAlarm ? (
        <CheckboxWithHint
          checked={form.generatesAlarm}
          onChange={(v) => setForm((f) => ({ ...f, generatesAlarm: v }))}
          title="Gera alarme"
          hint="Quando este parâmetro para a máquina, abre um alarme (apenas com a linha em produção)."
        />
      ) : null}

      {showIsRestriction ? (
        <CheckboxWithHint
          checked={form.isRestriction}
          onChange={(v) => setForm((f) => ({ ...f, isRestriction: v }))}
          title="É restrição operacional?"
          // Spec 080 — era o ÚNICO controle do formulário sem texto de ajuda, e essa ausência era a
          // marca de um campo que salvava e não fazia nada. Agora ele para a máquina de verdade.
          hint={
            category === "BOOLEAN"
              ? "Enquanto o valor for 1 (verdadeiro), a máquina fica parada e abre evento de parada. Ela volta sozinha quando o sinal normalizar."
              : "Sair dos limites abaixo passa a PARAR a máquina, não só alarmar. Ela volta sozinha quando o valor voltar à faixa."
          }
        />
      ) : null}

      <VisualRoleFields form={form} setForm={setForm} />

      {showCounterFields ? (
        <CounterFields
          form={form}
          setForm={setForm}
          editing={editingId !== null}
          existingCounters={existingCounters}
          editingId={editingId}
        />
      ) : null}

      <div className="flex gap-2 pt-1">
        <Button size="sm" className="flex-1" onClick={onSubmit} disabled={submitDisabled}>
          {isPending ? "Salvando…" : editingId ? "Salvar" : "Criar"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={isPending}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function hasDuplicateKeys(keys: string[]): boolean {
  return new Set(keys).size !== keys.length;
}

interface CheckboxWithHintProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  hint?: string;
}

function CheckboxWithHint({ checked, onChange, title, hint }: CheckboxWithHintProps) {
  return (
    <label className="flex items-start gap-2 text-xs">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {title}
        {hint ? <span className="block text-[10px] text-muted-foreground">{hint}</span> : null}
      </span>
    </label>
  );
}

function sourceLabel(s: SourceKind): string {
  const labels: Record<SourceKind, string> = {
    MANUAL: "Manual",
    SENSOR: "Sensor",
    INTEGRATION: "Integração",
    IMPORT: "Importação",
    CALCULATED: "Calculado",
  };
  return labels[s];
}

export type { ParameterCategory };
